import { useMemo, useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  Calendar, Stethoscope, UserRound, Home, Heart, MessageCircle,
  Wallet, Eye, EyeOff, Bell, ArrowRight, Clock, MapPin, ArrowUpRight,
} from 'lucide-react'
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { usePatientAuth } from '@hooks/usePatientAuth'
import { patientProfileService } from '@lib/supabase/patientProfile.service'
import { appointmentService, Appointment } from '@lib/supabase/appointment.service'
import { chatService } from '@lib/supabase/chat.service'
import { providerService } from '@lib/supabase/provider.service'
import { notificationService } from '@lib/supabase/notification.service'
import { queryKeys } from '@lib/queryKeys'
import Button from '@components/web/Button'
import { clsx } from 'clsx'
import { SetupGuideFloatingButton, SetupGuidePanel, SetupGuideRail } from '@components/web/SetupGuide'
import { setupGuideService, type SetupGuideItemKey } from '@lib/supabase/setupGuide.service'

// ---- helpers ----------------------------------------------------------------
const formatCurrency = (n: number) =>
  new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', maximumFractionDigits: 2 }).format(n)

const formatTime12h = (t?: string | null) => {
  if (!t || t === 'TBD') return 'TBD'
  const [h, m] = t.split(':').map(Number)
  const ampm = h >= 12 ? 'PM' : 'AM'
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${ampm}`
}

const aptDate = (apt: Appointment): Date => {
  const d = new Date(apt.scheduled_date)
  if (apt.scheduled_time) {
    const [h, m] = apt.scheduled_time.split(':').map(Number)
    if (!isNaN(h)) { d.setHours(h, m, 0, 0); return d }
  }
  d.setHours(23, 59, 59, 999)
  return d
}

const getInitials = (name: string) =>
  name.trim().split(' ').slice(0, 2).map((w) => w[0]).join('').toUpperCase()

const scrollToCareOptions = () => {
  document.getElementById('patient-service-cards')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

// ---- service cards metadata -------------------------------------------------
const DERMATOLOGY_ICON = '/dermatology.png'

const SERVICE_CARDS = [
  { type: 'Doctor Visit',      label: 'Doctor Visit',      desc: 'In-home doctor consultation and medical assessment',          icon: Stethoscope,   color: '#2563EB' },
  { type: 'Nurse Care',        label: 'Nurse Care',        desc: 'Skilled nursing care and ongoing health monitoring',          icon: UserRound,     color: '#059669' },
  { type: 'Home Care',         label: 'Home Care',         desc: 'Daily care support and assisted living services at home',     icon: Home,          color: '#7C3AED' },
  { type: 'Specialized Care',  label: 'Specialized Care',  desc: 'Specialist-led care tailored to more complex health needs',   icon: Heart,         color: '#DC2626' },
  { type: 'Dermatology',       label: 'Dermatology',       desc: 'In-home skin, hair, and nail assessment and care',             icon: 'dermatology', color: '#0F766E' },
  { type: 'Chat Consultation', label: 'Chat Consultation', desc: 'A 30-minute real-time chat consultation with a provider',      icon: MessageCircle, color: '#0EA5E9' },
]

// ---- status badge -----------------------------------------------------------
function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    scheduled: 'bg-blue-100 text-blue-700',
    requested: 'bg-yellow-100 text-yellow-700',
    completed: 'bg-green-100 text-green-700',
    cancelled: 'bg-red-100 text-red-700',
    rejected: 'bg-red-100 text-red-700',
  }
  return (
    <span className={clsx('text-xs px-2 py-0.5 rounded-full font-medium capitalize', map[status?.toLowerCase()] ?? 'bg-muted text-muted-foreground')}>
      {status}
    </span>
  )
}

// ---- main component ---------------------------------------------------------
export default function PatientDashboard() {
  const navigate = useNavigate()
  const { patient } = usePatientAuth()
  const [balanceVisible, setBalanceVisible] = useState(false)
  const [isGuideOpen, setIsGuideOpen] = useState(false)
  const queryClient = useQueryClient()

  const hour = new Date().getHours()
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening'
  const greetingEmoji = hour < 12 ? '⛅' : hour < 17 ? '☀️' : '🌙'

  const [
    walletBalanceQuery,
    appointmentsQuery,
    chatAppointmentsQuery,
    unreadNotificationCountQuery,
  ] = useQueries({
    queries: [
      {
        queryKey: queryKeys.patient.walletBalance(patient?.id),
        queryFn: patientProfileService.getWalletBalance,
        enabled: !!patient?.id,
      },
      {
        queryKey: queryKeys.patient.appointments(patient?.id),
        queryFn: () => appointmentService.getPatientAppointments(undefined, undefined, 'Chat Consultation'),
        enabled: !!patient?.id,
      },
      {
        queryKey: queryKeys.patient.appointments(patient?.id, undefined, 'Chat Consultation'),
        queryFn: () => appointmentService.getPatientAppointments(undefined, 'Chat Consultation'),
        enabled: !!patient?.id,
      },
      {
        queryKey: queryKeys.patient.unreadNotifications(patient?.id),
        queryFn: () => notificationService.getUnreadCountForPatient(patient!.id),
        enabled: !!patient?.id,
      },
    ],
  })

  const appointments = appointmentsQuery.data ?? []
  const chatAppointmentsRaw = chatAppointmentsQuery.data ?? []
  const balance = walletBalanceQuery.data ?? 0
  const unreadNotificationCount = unreadNotificationCountQuery.data ?? 0

  const setupGuideQuery = useQuery({
    queryKey: queryKeys.patient.setupGuide(patient?.id),
    queryFn: setupGuideService.getPatientGuideStatus,
    enabled: !!patient?.id,
  })

  const setupGuideStatus = setupGuideQuery.data ?? null

  const topProvidersQuery = useQuery({
    queryKey: queryKeys.patient.topProviders(patient?.id),
    queryFn: async () => {
      const topProviders = (await providerService.getAllProviders()).slice(0, 20)
      const withRatings = await Promise.all(
        topProviders.map(async (provider) => {
          const stats = await providerService.getProviderRatingStats(provider.id)
          return { ...provider, rating: stats.avgRating, totalReviews: stats.totalReviews }
        })
      )

      return withRatings
        .filter((provider) => provider.rating >= 4)
        .sort((left, right) => right.rating - left.rating)
        .slice(0, 6)
    },
    enabled: !!patient?.id,
  })

  const topProviders = topProvidersQuery.data ?? []

  const nextAppointmentCandidate = useMemo(() => {
    const now = new Date()
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    const upcoming = appointments
      .filter((appointment) => {
        const status = appointment.status?.toLowerCase()
        const dateTime = aptDate(appointment)
        if (status === 'scheduled') return dateTime >= today
        if (status === 'requested') return dateTime >= now
        return false
      })
      .sort((left, right) => aptDate(left).getTime() - aptDate(right).getTime())

    return upcoming[0] ?? null
  }, [appointments])

  const nextAppointmentProviderQuery = useQuery({
    queryKey: queryKeys.providers.byId(nextAppointmentCandidate?.provider_id),
    queryFn: () => providerService.getProviderById(nextAppointmentCandidate!.provider_id),
    enabled: !!nextAppointmentCandidate?.provider_id,
  })

  const sortedChatAppointments = useMemo(() => {
    const rank: Record<string, number> = {
      scheduled: 0,
      requested: 1,
      completed: 2,
      cancelled: 3,
      rejected: 4,
    }

    return [...chatAppointmentsRaw]
      .sort((left, right) => {
        const leftStatus = left.status?.toLowerCase() ?? ''
        const rightStatus = right.status?.toLowerCase() ?? ''
        const rankDelta = (rank[leftStatus] ?? 99) - (rank[rightStatus] ?? 99)
        if (rankDelta !== 0) return rankDelta
        return (right.created_at || '').localeCompare(left.created_at || '')
      })
      .filter((appointment) => !['cancelled', 'rejected'].includes(appointment.status?.toLowerCase() ?? ''))
      .slice(0, 3)
  }, [chatAppointmentsRaw])

  const chatProviderIds = useMemo(
    () => Array.from(new Set(sortedChatAppointments.map((appointment) => appointment.provider_id).filter(Boolean))).sort(),
    [sortedChatAppointments]
  )

  const chatProviderMapQuery = useQuery({
    queryKey: queryKeys.providers.byIds(chatProviderIds),
    queryFn: () => providerService.getProvidersByIds(chatProviderIds),
    enabled: chatProviderIds.length > 0,
  })

  const latestIncomingMapQuery = useQuery({
    queryKey: queryKeys.patient.latestIncomingMessages(patient?.id),
    queryFn: () => chatService.getLatestIncomingProviderMessageMap(patient!.id),
    enabled: !!patient?.id,
  })

  const nextAppointment = nextAppointmentCandidate
    ? { apt: nextAppointmentCandidate, provider: nextAppointmentProviderQuery.data ?? null }
    : null

  const chatAppointments = sortedChatAppointments.map((appointment) => ({
    apt: appointment,
    provider: chatProviderMapQuery.data?.[appointment.provider_id] ?? null,
  }))

  const unreadChatKeys = useMemo(() => {
    if (!patient?.id) return new Set<string>()
    return chatService.getUnreadConversationKeys(patient.id, chatAppointmentsRaw, latestIncomingMapQuery.data ?? {})
  }, [patient?.id, chatAppointmentsRaw, latestIncomingMapQuery.data])

  useEffect(() => {
    if (!patient?.id) return
    const subscription = notificationService.subscribeToPatientNotifications(patient.id, () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.patient.unreadNotifications(patient.id) })
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [patient?.id, queryClient])

  const handleGuideAction = (itemKey: SetupGuideItemKey) => {
    setIsGuideOpen(false)

    switch (itemKey) {
      case 'profile-photo':
        navigate('/patient/profile')
        break
      case 'address':
        navigate('/patient/profile', { state: { view: 'address' } })
        break
      case 'emergency-contacts':
        navigate('/patient/profile', { state: { view: 'emergency-contacts' } })
        break
      default:
        break
    }
  }

  return (
    <div className="relative p-4 pb-24 sm:p-6 sm:pb-24 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate('/patient/profile')}
            className="w-12 h-12 rounded-full border border-border bg-card overflow-hidden shrink-0 hover:bg-muted transition-colors"
            title="Open profile"
            aria-label="Open profile"
          >
            {patient?.profile_image_url ? (
              <img src={patient.profile_image_url} alt="Profile" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-primary/10 text-primary font-bold text-sm">
                {patient?.name ? patient.name.split(' ').slice(0, 2).map((part) => part[0]).join('').toUpperCase() : 'PT'}
              </div>
            )}
          </button>

          <div>
            <h1 className="text-2xl font-bold text-foreground">
              {greeting} {greetingEmoji}
            </h1>
            <p className="text-muted-foreground text-base font-medium mt-0.5">{patient?.name ?? 'Patient'}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button title="Notifications" onClick={() => navigate('/patient/notifications')} className="relative p-2 hover:bg-muted rounded-lg transition-colors">
            <Bell size={18} className="text-muted-foreground" />
            {unreadNotificationCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-4 h-4 px-1 bg-red-500 text-white text-[10px] font-bold flex items-center justify-center rounded-full">
                {unreadNotificationCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Wallet Card */}
      <div className="bg-primary border p-6 text-primary-foreground shadow-md">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <Wallet size={18} className="opacity-80" />
            <span className="text-sm opacity-80 font-medium">Wallet Balance</span>
          </div>
          <button
            onClick={() => setBalanceVisible(!balanceVisible)}
            className="p-1 rounded hover:bg-white/10 transition-colors"
          >
            {balanceVisible ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
        <p className="text-3xl font-bold mt-1">
          {balanceVisible ? formatCurrency(balance) : '₦ ••••••'}
        </p>
        <div className="flex gap-3 mt-4">
          <Link
            to="/patient/profile"
            state={{ view: 'wallet' }}
            className="flex-1 text-center text-xs font-semibold py-2 rounded-lg bg-white/20 hover:bg-white/30 transition-colors"
          >
            Top Up
          </Link>
          <Link
            to="/patient/profile"
            state={{ view: 'wallet' }}
            className="flex-1 text-center text-xs font-semibold py-2 rounded-lg bg-white/20 hover:bg-white/30 transition-colors"
          >
            History
          </Link>
        </div>
      </div>

      {setupGuideStatus ? (
        <SetupGuideRail
          title="Setup Guide"
          status={setupGuideStatus}
          onOpen={() => setIsGuideOpen(true)}
          onAction={handleGuideAction}
        />
      ) : null}

      {/* Services */}
      <div id="patient-service-cards">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold text-foreground">What care do you need?</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Discover verified health providers.
        </p>
        <div className="grid gap-3 md:grid-cols-2">
          {SERVICE_CARDS.map((svc) => {
            const Icon = svc.icon === 'dermatology' ? null : svc.icon
            return (
              <Link
                key={svc.type}
                to={`/patient/services/${encodeURIComponent(svc.type)}`}
                className={clsx(
                  'group border border-border p-4 no-underline transition-all hover:shadow-sm',
                )}
                style={{ backgroundColor: `${svc.color}12` }}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="h-12 w-12 shrink-0 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: svc.color }}>
                    {svc.type === 'Dermatology' ? (
                      <img
                        src={DERMATOLOGY_ICON}
                        alt=""
                        aria-hidden="true"
                        className="h-6 w-6 object-contain"
                        style={{ filter: 'brightness(0) invert(1)' }}
                      />
                    ) : Icon ? (
                      <Icon size={24} color="#FFFFFF" />
                    ) : null}
                  </div>
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/80 text-slate-900 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5">
                    <ArrowUpRight size={18} />
                  </div>
                </div>
                <div className="mt-5 min-w-0">
                  <p className="text-base font-semibold text-foreground">{svc.label}</p>
                  <p className="mt-1.5 text-sm leading-6 text-muted-foreground">{svc.desc}</p>
                </div>
              </Link>
            )
          })}
        </div>
      </div>

      {/* Next Appointment */}
      {nextAppointment ? (
        <div className="bg-card border border-border border p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-foreground">Next Appointment</h2>
            <StatusBadge status={nextAppointment.apt.status} />
          </div>
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
              {nextAppointment.provider?.profile_image_url ? (
                <img src={nextAppointment.provider.profile_image_url} alt="" className="w-12 h-12 rounded-full object-cover" />
              ) : (
                <span className="text-primary font-bold text-sm">
                  {nextAppointment.provider?.name ? getInitials(nextAppointment.provider.name) : '?'}
                </span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-foreground truncate">
                {nextAppointment.provider?.name ?? 'Provider'}
              </p>
              <p className="text-sm text-muted-foreground">{nextAppointment.provider?.specialty ?? 'Healthcare Provider'}</p>
              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Calendar size={12} />
                  {new Date(nextAppointment.apt.scheduled_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                </div>
                {nextAppointment.apt.scheduled_time && (
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Clock size={12} />
                    {formatTime12h(nextAppointment.apt.scheduled_time)}
                  </div>
                )}
                {nextAppointment.apt.location && (
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <MapPin size={12} />
                    {nextAppointment.apt.location}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="mt-4 pt-3 border-t border-border">
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={() => navigate(`/patient/appointments`)}
            >
              View All Appointments
            </Button>
          </div>
        </div>
      ) : (
        <div className="bg-card border border-dashed border-border p-6 text-center">
          <Calendar size={32} className="mx-auto text-muted-foreground/50 mb-2" />
          <p className="text-sm font-medium text-foreground">No upcoming appointments</p>
          <p className="text-xs text-muted-foreground mt-1">Book an appointment to get started</p>
          <Button className="mt-4" size="sm" onClick={scrollToCareOptions}>
            Book Now
          </Button>
        </div>
      )}

      <div className="border border-border bg-card p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="font-semibold text-foreground">Chats</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Reopen active and recent provider chats.
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate('/patient/chats')}
            className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
          >
            See all
            <ArrowRight size={14} />
          </button>
        </div>

        {chatAppointments.length > 0 ? (
          <div className="mt-4 space-y-3">
            {chatAppointments.map(({ apt, provider }) => (
              <button
                key={apt.id}
                type="button"
                onClick={() => navigate(`/patient/chat/${apt.id}`, { state: { backPath: '/patient/dashboard' } })}
                className="flex w-full items-start gap-3 border border-border bg-background p-3 text-left transition-colors hover:bg-muted/40"
              >
                <div className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-sky-50 text-sky-600">
                  {provider?.profile_image_url ? (
                    <img src={provider.profile_image_url} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <MessageCircle size={20} />
                  )}
                </div>

                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-foreground">
                        {provider?.name ?? 'Healthcare Provider'}
                      </p>
                      <p className="truncate text-xs text-muted-foreground">
                        {provider?.specialty ?? 'Chat Consultation'}
                      </p>
                    </div>
                    <StatusBadge status={apt.status} />
                  </div>

                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Calendar size={12} />
                      {new Date(apt.scheduled_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </div>
                    {apt.scheduled_time ? (
                      <div className="flex items-center gap-1">
                        <Clock size={12} />
                        {formatTime12h(apt.scheduled_time)}
                      </div>
                    ) : null}
                  </div>

                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    {unreadChatKeys.has(chatService.getConversationKey(apt.id, apt.conversation_id)) ? (
                      <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-emerald-700">
                        New message
                      </span>
                    ) : null}
                    <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary">
                    Open Chat
                    <ArrowUpRight size={14} />
                    </span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        ) : (
          <div className="mt-4 border border-dashed border-border p-5 text-center">
            <MessageCircle size={28} className="mx-auto text-muted-foreground/40 mb-2" />
            <p className="text-sm font-medium text-foreground">No chat consultations yet</p>
            <p className="mt-1 text-xs text-muted-foreground">Book a chat consultation and it will stay here for quick access.</p>
            <button
              type="button"
              onClick={() => navigate('/patient/services/Chat%20Consultation')}
              className="mt-3 text-sm font-medium text-primary hover:underline"
            >
              Start a chat consultation
            </button>
          </div>
        )}
      </div>


      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold text-foreground">Top Rated Providers</h2>
          <span className="text-sm text-muted-foreground">Highly reviewed care professionals</span>
        </div>

        {topProvidersQuery.isLoading ? (
          <div className="space-y-3">
            {[...Array(3)].map((_, index) => (
              <div key={index} className="h-24 bg-muted rounded-xl animate-pulse" />
            ))}
          </div>
        ) : topProviders.length === 0 ? (
          <div className="border border-border bg-card p-5 text-center">
            <p className="text-sm text-muted-foreground">No top providers available yet.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {topProviders.map((provider) => (
              <button
                key={provider.id}
                type="button"
                onClick={() => navigate(`/patient/services/provider/${provider.id}`)}
                className="w-full text-left bg-card border border-border p-4 hover:border-primary/40 hover:shadow-sm transition-all"
              >
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center shrink-0 overflow-hidden">
                    {provider.profile_image_url ? (
                      <img src={provider.profile_image_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-primary font-bold text-sm">{getInitials(provider.name)}</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-foreground truncate">{provider.name}</p>
                    <p className="text-sm text-muted-foreground">{provider.specialty}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs font-medium">⭐ {provider.rating.toFixed(1)}</span>
                      <span className="text-xs text-muted-foreground">({provider.totalReviews})</span>
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={() => navigate('/patient/profile', { state: { view: 'support' } })}
        className="fixed bottom-4 right-4 z-20 inline-flex items-center gap-2 rounded-full bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground shadow-[0_14px_30px_rgba(4,112,79,0.28)] transition-transform hover:-translate-y-0.5 hover:bg-primary/95"
      >
        <MessageCircle size={18} />
        Get Help
      </button>
      <SetupGuideFloatingButton remainingCount={setupGuideStatus?.remainingCount ?? 0} onOpen={() => setIsGuideOpen(true)} />
      <SetupGuidePanel
        open={isGuideOpen}
        title="Patient Setup"
        status={setupGuideStatus}
        onClose={() => setIsGuideOpen(false)}
        onAction={handleGuideAction}
      />
    </div>
  )
}
